import { Alert } from "@/core/components/ui/alert/alert";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/core/components/ui/card";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { useBusiness } from "@/core/business";
import { useGetMyUser } from "@/user/query";
import { getPayrollRosterApiErrorMessage } from "./api-errors";
import { PayrollRosterEditor } from "./payroll-roster-editor";
import { PayrollRosterLoadingState } from "./payroll-roster-loading-state";
import {
  canManagePayrollRoster,
  canSyncPayrollRosterTechnicians,
  useCanViewPayrollRoster,
} from "./permissions";
import { usePayrollRoster } from "./query";
import { RosterReadOnlyView } from "./roster-read-only-view";

const PayrollRosterPage = () => {
  const {
    businessId,
    selectedBusiness,
    isLoading: businessLoading,
  } = useBusiness();
  const { data: user, isLoading: userLoading } = useGetMyUser();
  const {
    accessQuery,
    canViewRoster,
    isLoading: rosterAccessLoading,
  } = useCanViewPayrollRoster(selectedBusiness, user);
  const rosterQuery = usePayrollRoster(canViewRoster ? businessId : null);

  if (businessLoading || userLoading || rosterAccessLoading) {
    return <PayrollRosterLoadingState />;
  }

  if (!canViewRoster) {
    return (
      <Stack fullWidth className="mx-auto max-w-[1680px] p-6 lg:p-8">
        <Card>
          <CardHeader>
            <CardTitle>
              {accessQuery.isError
                ? "Payroll access could not be checked"
                : "You do not have access to this roster"}
            </CardTitle>
            <CardDescription>
              {accessQuery.isError
                ? "Refresh the page to try again."
                : "Ask an admin to grant you roster access."}
            </CardDescription>
          </CardHeader>
        </Card>
      </Stack>
    );
  }

  if (!rosterQuery.isFetchedAfterMount && !rosterQuery.isError) {
    return <PayrollRosterLoadingState />;
  }

  const isRosterReady = Boolean(
    businessId && rosterQuery.isSuccess && rosterQuery.isFetchedAfterMount,
  );
  const canEdit =
    accessQuery.data?.roster_visibility === "full" &&
    canManagePayrollRoster(selectedBusiness, user) &&
    (!rosterQuery.data?.team_scope_active || accessQuery.data?.mode === "full");

  return (
    <Stack
      gap="lg"
      align="stretch"
      fullWidth
      className="mx-auto h-full min-h-0 max-w-[1680px] p-6 lg:p-8"
    >
      {(!isRosterReady || !canEdit) && (
        <Stack gap="none">
          <Typography variant="titleW2">Payroll roster</Typography>
          <Typography className="mt-2" textColor="secondary">
            Manage the people and attributes used to calculate payroll.
          </Typography>
        </Stack>
      )}

      {rosterQuery.isError && (
        <Alert variant="destructive">
          Payroll roster could not be loaded.{" "}
          {getPayrollRosterApiErrorMessage(rosterQuery.error)}
        </Alert>
      )}

      {rosterQuery.isSuccess && !rosterQuery.data && (
        <Card>
          <CardHeader>
            <CardTitle>No payroll roster configured</CardTitle>
            <CardDescription className="text-base">
              Ask your Volca contact to configure the roster fields for this
              business before adding people.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {isRosterReady &&
        businessId &&
        rosterQuery.data &&
        (canEdit ? (
          <PayrollRosterEditor
            key={`${businessId}-${rosterQuery.data.baseline?.version_number ?? rosterQuery.data.version_number ?? "current"}`}
            roster={rosterQuery.data}
            businessId={businessId}
            canSyncTechnicians={canSyncPayrollRosterTechnicians(
              selectedBusiness,
              user,
            )}
          />
        ) : (
          <RosterReadOnlyView
            key={businessId}
            businessId={businessId}
            roster={rosterQuery.data}
          />
        ))}
    </Stack>
  );
};

export default PayrollRosterPage;
